import type { ReactNode } from 'react';
import { Tabs, type TabItem } from '~/components/ui/tabs';

/**
 * Card Header Component — also the widget header (DESIGN.md §7.2).
 * Left: tabs (an in-card filter) OR icon, title, description and status badge.
 * Right: `actions`.
 */

export interface CardHeaderProps {
  /** An in-card filter shown instead of the title; needs `activeTab` and `onTabChange`. */
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** With `tabs`: ids for the tab set; the caller renders the matching `role="tabpanel"` (see `Tabs`). */
  tabsIdPrefix?: string;
  title?: string;
  /** One line of sub-copy under the title. */
  description?: string;
  icon?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  /** Id on the heading, for `aria-labelledby`. */
  id?: string;
  /**
   * Status of what the card holds — rendered on the LEFT, after the title. Belongs
   * there rather than among the `actions`: it is a statement about the card,
   * not a control, and reads as another button when it sits in the action row.
   */
  badge?: ReactNode;
  actions?: ReactNode;
}

export function CardHeader({
  tabs,
  activeTab,
  onTabChange,
  tabsIdPrefix,
  title,
  description,
  icon,
  headingLevel = 3,
  id,
  badge,
  actions,
}: CardHeaderProps) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Tabs on the left */}
        {tabs && tabs.length > 0 ? (
          <Tabs
            variant="pill"
            tabs={tabs}
            activeId={activeTab ?? tabs[0].id}
            onChange={(id) => onTabChange?.(id)}
            ariaLabel={title}
            idPrefix={tabsIdPrefix}
          />
        ) : title || badge || icon ? (
          <div className="flex items-center gap-3">
            {icon && <span className="shrink-0 text-gray-500 dark:text-gray-400">{icon}</span>}
            <div>
              {title && (
                <Heading
                  id={id}
                  className="text-base font-semibold text-gray-800 dark:text-white/90"
                >
                  {title}
                </Heading>
              )}
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
              )}
            </div>
            {badge}
          </div>
        ) : (
          <div />
        )}

        {/* Actions/buttons on the right */}
        {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}
